import { Item, Move, MoveLine, Stock, Warehouse, WarehouseLocation, WarehouseLocationStorageType } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { DataFetcherFunction } from '@/compiler/types';
import baseAxios, { fetchPaginatedDataFunction } from '@/helpers/forms/axios';

type FetchLocationReturnType = WarehouseLocation<
    Warehouse,
    MoveLine<Move<undefined, Item>>,
    Stock<undefined, Item>
>;

type LocationExpand = 'stocks';

interface WarehouseLocationsApiReturn {
    fetchLocations: (warehouseId: number, expand?: LocationExpand[]) => DataFetcherFunction<WarehouseLocation<undefined, undefined, Stock<undefined, undefined>>>
    fetchLocation: (warehouseId: number, locationUuid: string) => Promise<FetchLocationReturnType>
    createLocation: (warehouseId: number, data: { name: string; storage_type: WarehouseLocationStorageType; storage_address?: { aisle?: string; section?: string; level?: string; position?: string } }) => Promise<WarehouseLocation>
    exportLocations: (warehouseId: number) => Promise<string>
    deleteLocation: (warehouseId: number, locationUuid: string, note?: string | null) => Promise<void>
}

export default (axios: AxiosInstance = baseAxios): WarehouseLocationsApiReturn => ({
    fetchLocations: (warehouseId: number, expand: LocationExpand[] = []): DataFetcherFunction<WarehouseLocation<undefined, undefined, Stock<undefined, undefined>>> => {
        return fetchPaginatedDataFunction<WarehouseLocation<undefined, undefined, Stock<undefined, undefined>>>(
            `/api/warehouses/${warehouseId}/locations`,
            (uri) => {
                expand.forEach(value => uri.searchParams.append('expand[]', value));
                return uri;
            },
            axios,
        );
    },

    fetchLocation: (warehouseId: number, locationUuid: string): Promise<FetchLocationReturnType> => axios
        .get<FetchLocationReturnType>(`/api/warehouses/${warehouseId}/locations/${locationUuid}`)
        .then(response => response.data),

    createLocation: (warehouseId: number, data: {
        name: string
        storage_type: WarehouseLocationStorageType
        storage_address?: { aisle?: string; section?: string; level?: string; position?: string }
    }): Promise<WarehouseLocation> => axios
        .post<WarehouseLocation>(`/api/warehouses/${warehouseId}/locations`, data)
        .then(response => response.data),

    exportLocations: async (warehouseId: number): Promise<string> => {
        const response = await axios
            .get<{ downloadUrl: string }>(`/api/warehouses/${warehouseId}/locations/export`);

        return response.data.downloadUrl;
    },

    deleteLocation: async (warehouseId: number, locationUuid: string, note: string | null = null): Promise<void> => {
        await axios.delete<void>(`/api/warehouses/${warehouseId}/locations/${locationUuid}`, { data: { note } });
    },
});
